import apiClient from '@/services/api'
import util from '@/util'

export default {
  async getTimeOffs({ commit, rootState }, payload) {
    const manager_scopes = rootState.applicationState.associate.manager_scopes
    const currentPaymentType = rootState.applicationState.currentPaymentTypeName

    try {
      const { data } = await apiClient.get(
        '/api/time_off?fields={*,associate{first_name,last_name,photo_url,is_verified,role,associate_number,id,payment_info},-comment,-files,-signature,-review_payments,-review_days,-parent,items{date},-origin,-need_payments}',
        {
          params: {
            ...payload,
            project_id: rootState.applicationState.currentProject.id,
            manager_scopes: encodeURIComponent(JSON.stringify(manager_scopes)),
            payment_types: currentPaymentType === 'All' ? payload.payment_types : [currentPaymentType.toLowerCase()],
            date_range: {
              start: payload.start,
              end: payload.end
            }
          },
          cancelPreviousRequests: true
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_TIME_OFFS', data)
    } catch (error) {
      throw error.message
    }
  },
  async getTimeOffStatistic({ commit, rootState, state }, { associate_id, type }) {
    const params = {
      associates: [associate_id],
      types: [type],
      project_id: rootState.applicationState.currentProject.id,
      size: 1000
    }

    try {
      const { data } = await apiClient.get(
        '/api/time_off?fields={items{date,shift{pay_code,internal_payment_info{ready_for_pay_status}}}}',
        {
          params: {
            ...params,
            project_id: rootState.applicationState.currentProject.id,
            date_range: util.getDateRange(1, 'year'),
            size: 1000
          }
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      const statistic = data.items.map(item => item.items).flat()

      commit('SET_TIME_OFF', {
        ...state.timeOff,
        statistic
      })
    } catch (error) {
      throw error.message
    }
  },
  async getTimeOff({ commit }, { id }) {
    if (!id) {
      throw new Error('Time off request is not selected.')
    }

    try {
      const [timeOffResponse, shiftCheckResponse] = await Promise.all([
        apiClient.get(
          `api/time_off/${id}?fields={*,associate{first_name,last_name,photo_url,is_verified,role,associate_number,id,payment_info},review_days{*, author{first_name,last_name,photo_url,associate_number,is_system}},review_payments{*, author{first_name,last_name,photo_url,associate_number,is_system}},items{*,shift{id,internal_payment_info{ready_for_pay_status},status}}}`,
          { skipSentryHttpError: true }
        ),
        apiClient.get(`api/time_off/${id}/items/shift/check`, { skipSentryHttpError: true })
      ])

      const timeOffData = timeOffResponse.data
      const shiftCheckData = shiftCheckResponse.data

      if (timeOffData.error_code && timeOffData.error_code !== '0') {
        throw new Error(`${timeOffData.msg}`)
      }

      if (shiftCheckData.error_code && shiftCheckData.error_code !== '0') {
        throw new Error(`${shiftCheckData.msg}`)
      }

      commit('SET_TIME_OFF', {
        ...timeOffData.time_off,
        conflicts: shiftCheckData.items
      })
    } catch (error) {
      throw error.message
    }
  },
  async updateTimeOffAttachments({ dispatch }, { id, files }) {
    try {
      const { data } = await apiClient.post(`/api/time_off/${id}/add_files`, {
        files
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while uploading attachments.'}`)
      }

      await dispatch('getTimeOff', { id })
    } catch (error) {
      throw error.message
    }
  },
  async getTimeOffByShift(_, { id }) {
    try {
      const { data } = await apiClient.get(`/api/time_off/shift/${id}`)

      if (data?.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data?.time_off_id || undefined
    } catch (error) {
      throw error.message
    }
  },
  async approveDaysTimeOff(_, { id, items, signature_id, type }) {
    try {
      const { data } = await apiClient.post(`api/time_off/${id}/days/${type}`, {
        items,
        signature: signature_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async deleteDaysTimeOff(_, { ids }) {
    try {
      const { data } = await apiClient.delete('/api/time_off/items/delete', {
        params: {
          ids
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async approveShiftsTimeOff(_, { id, items, signature_id }) {
    try {
      const { data } = await apiClient.post(`api/time_off/${id}/shifts/approve`, {
        items,
        signature: signature_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async checkConflict(_, { id }) {
    try {
      const { data } = await apiClient.get(`api/time_off/${id}/items/shift/check`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.items
    } catch (error) {
      throw error.message
    }
  },
  async deleteTimeOff(_, { id }) {
    try {
      const { data } = await apiClient.delete(`/api/time_off/${id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async approveTimeOffs(_, { ids, signature }) {
    try {
      const { data } = await apiClient.post('/api/time_off/approve/all', {
        ids,
        signature
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async declineTimeOffs(_, { ids, signature }) {
    try {
      const { data } = await apiClient.post('/api/time_off/decline/all', {
        ids,
        signature
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async deleteTimeOffs(_, { ids }) {
    try {
      const { data } = await apiClient.post('/api/time_off/delete/all', {
        ids
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  }
}
